import { beginRequest } from "./requestActivity";

const axiosReleaseKey = Symbol("globalLoaderRelease");
const installations = new WeakMap();
const responseBodyReaders = ["arrayBuffer", "blob", "formData", "json", "text"];

const finishAxiosRequest = (value) => {
  value?.config?.[axiosReleaseKey]?.();
  return value;
};

const scheduleUnusedResponseRelease = (release) => {
  if (typeof requestAnimationFrame === "function") {
    const frame = requestAnimationFrame(release);
    return () => cancelAnimationFrame(frame);
  }

  const timer = setTimeout(release, 0);
  return () => clearTimeout(timer);
};

const trackResponseBody = (response, release) => {
  let readerCount = 0;
  let cancelUnusedResponseRelease;

  responseBodyReaders.forEach((readerName) => {
    if (typeof response?.[readerName] !== "function") return;

    const originalReader = response[readerName].bind(response);
    response[readerName] = (...args) => {
      cancelUnusedResponseRelease?.();
      try {
        return Promise.resolve(originalReader(...args)).finally(release);
      } catch (error) {
        release();
        throw error;
      }
    };
    readerCount += 1;
  });

  if (readerCount === 0) {
    release();
  } else {
    cancelUnusedResponseRelease = scheduleUnusedResponseRelease(release);
  }

  return response;
};

export const installRequestTracking = ({ fetchTarget, axiosInstance }) => {
  const existing = installations.get(fetchTarget);
  if (existing?.axiosInstance === axiosInstance) {
    existing.references += 1;
    return () => {
      existing.references -= 1;
      if (existing.references === 0) existing.uninstall();
    };
  }

  const originalFetch = fetchTarget.fetch;
  fetchTarget.fetch = (input, options) => {
    const {
      globalLoader = false,
      skipGlobalLoader = false,
      ...nativeOptions
    } = options || {};
    if (!globalLoader || skipGlobalLoader) {
      return originalFetch.call(fetchTarget, input, nativeOptions);
    }

    const release = beginRequest();
    try {
      return Promise.resolve(
        originalFetch.call(fetchTarget, input, nativeOptions)
      ).then(
        (response) => trackResponseBody(response, release),
        (error) => {
          release();
          throw error;
        }
      );
    } catch (error) {
      release();
      throw error;
    }
  };

  const requestInterceptor = axiosInstance.interceptors.request.use(
    (config) => {
      if (config.globalLoader && !config.skipGlobalLoader) {
        config[axiosReleaseKey] = beginRequest();
      }
      return config;
    },
    (error) => {
      finishAxiosRequest(error);
      return Promise.reject(error);
    }
  );

  const responseInterceptor = axiosInstance.interceptors.response.use(
    (response) => {
      finishAxiosRequest(response);
      return response;
    },
    (error) => {
      finishAxiosRequest(error);
      return Promise.reject(error);
    }
  );

  const installation = {
    axiosInstance,
    references: 1,
    uninstall: () => {
      if (installations.get(fetchTarget) !== installation) return;
      fetchTarget.fetch = originalFetch;
      axiosInstance.interceptors.request.eject(requestInterceptor);
      axiosInstance.interceptors.response.eject(responseInterceptor);
      installations.delete(fetchTarget);
    },
  };
  installations.set(fetchTarget, installation);

  return () => {
    installation.references -= 1;
    if (installation.references === 0) installation.uninstall();
  };
};
